/* Updated AchievementImageUpload.tsx */
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MinusIcon, UploadCloudIcon } from 'lucide-react';

interface AchievementImageUploadProps {
  file?: File;
  achievementId?: string;
  imageUrl?: string | null;
  onChange: (file: File) => void;
  onRemove?: () => void;
  className?: string;
}

export const AchievementImageUpload: React.FC<AchievementImageUploadProps> = ({
  achievementId,
  imageUrl,
  file,
  onChange,
  onRemove,
  className = '',
}) => {
  const { t } = useTranslation(['auth', 'common', 'settings']);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = `achievement-upload-${achievementId || 'new'}`;
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (newFile: File) => {
    if (!newFile.type.startsWith('image/')) return;
    onChange(newFile);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFile = e.target.files?.[0];
    if (newFile) handleFile(newFile);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) handleFile(droppedFile);
  };

  const dragEvents = {
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: () => setIsDragging(false),
    onDrop: handleDrop,
  };

  return (
    <div className={`rounded-xl p-4 text-center bg-white ${className}`}>
      {file ? (
        <div className="space-y-2">
          <div className="flex justify-center">
            <UploadCloudIcon className="w-6 h-6" />
          </div>
          <p className="text-sm font-medium">1 {t('achievements.fileAdded', { ns: 'settings' })}</p>
          <div className="border-t border-gray-300"></div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 bg-white p-1 rounded">
              <img
                src={URL.createObjectURL(file)}
                alt="uploaded"
                width={60}
                className="rounded-full object-cover h-[60px]"
              />
              <div className="flex flex-col items-start">
                <p className="text-sm font-medium truncate max-w-[100px]">{file.name}</p>
                <p className="text-xs text-gray-500">{Math.round(file.size / 1024)} Ko</p>
              </div>
            </div>
            <button onClick={onRemove} className="hover:text-red-700">
              <MinusIcon className="w-6 h-6 p-1 bg-gray-100 rounded-full" />
            </button>
          </div>
        </div>
      ) : imageUrl ? (
        <div className="space-y-2">
          <div className="flex justify-center">
            <UploadCloudIcon className="w-6 h-6" />
          </div>
          <p className="text-sm font-medium">
            <span className="text-sm font-medium">
              1 {t('achievements.fileAdded', { ns: 'settings' })}
            </span>
          </p>
          <div className="border-t border-gray-300"></div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 bg-white p-1 rounded">
              <img
                src={imageUrl}
                alt="Achievement preview"
                width={60}
                className="rounded-full object-cover h-[60px]"
              />
              <div className="flex flex-col items-start">
                <p className="text-sm font-medium truncate max-w-[100px]">
                  {t('achievements.currentImage', { ns: 'settings' })}
                </p>
              </div>
            </div>
            <button
              onClick={onRemove}
              className="hover:text-red-700"
              title={t('achievements.removeImage', { ns: 'settings' })}
            >
              <MinusIcon className="w-6 h-6 p-1 bg-gray-100 rounded-full" />
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            {...dragEvents}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer border-2 border-dashed ${
              isDragging ? 'border-blue-500' : 'border-gray-300'
            } rounded-xl px-4 py-10 text-gray-500 hover:border-blue-500`}
          >
            <input
              type="file"
              accept="image/*"
              hidden
              ref={inputRef}
              id={inputId}
              onChange={handleFileChange}
            />
            <div className="flex flex-col items-center space-y-2">
              <UploadCloudIcon className="w-6 h-6 mx-auto text-secondary" />
              <p> {t('achievements.imageUploadHint', { ns: 'settings' })}</p>
            </div>
          </div>
          <p className="text-sm text-secondary mt-6">
            {t('achievements.imageFormatAllowed', { ns: 'settings' })}
            <br />
            {t('improveProfileStep.achievements.imageSize', { ns: 'auth' })}
          </p>
        </>
      )}
    </div>
  );
};
